import React from "react";
import { Box, Slider, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import 'dayjs/locale/en-gb';
import { LocalizationProvider } from "@mui/x-date-pickers";
import EditableTimeLabel from "@/pages/custom-components/CommonComponents/EditableTimeField";

// Assicurati che il path sia corretto

interface MinMaxSlidersProps {
    minValue: number;
    maxValue: number;
    minDistance?: number;
    onSliderChange: (type: 'min' | 'max', val: number) => void;
    disabled?: boolean;
}

const MIN_DISTANCE = 30;
const MIN_S = 0;
const MAX_S = 600;

const MinMaxSliders: React.FC<MinMaxSlidersProps> = ({
                                                         minValue,
                                                         maxValue,
                                                         onSliderChange,
                                                         minDistance = MIN_DISTANCE,
                                                         disabled = false
                                                     }) => {

    const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

    // Converte i secondi correnti in un oggetto Dayjs (partendo dall'inizio della giornata)
    const secondsToDayjs = (sec: number) => dayjs().startOf('day').add(sec || 0, 'second');

    const handleChangeMin = (event: Event, value: number | number[]) => {
        const v = Array.isArray(value) ? value[0] : value;
        onSliderChange('min', clamp(v, MIN_S, maxValue - minDistance));
    };

    const handleChangeMax = (event: Event, value: number | number[]) => {
        const v = Array.isArray(value) ? value[0] : value;
        onSliderChange('max', clamp(v, minValue + minDistance, MAX_S));
    };

    // Gestori per il TimeField
    const handleMinTimeChange = (newValue: Dayjs | null) => {
        if (newValue && newValue.isValid()) {
            const sec = newValue.minute() * 60 + newValue.second();
            onSliderChange('min', clamp(sec, MIN_S, maxValue - minDistance));
        }
    };

    const handleMaxTimeChange = (newValue: Dayjs | null) => {
        if (newValue && newValue.isValid()) {
            const sec = newValue.minute() * 60 + newValue.second();
            onSliderChange('max', clamp(sec, minValue + minDistance, MAX_S));
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <Box sx={{ flex: "2 1 700px", minWidth: 520 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        width: "100%",
                    }}
                >
                    {/* MIN */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 3, width: "100%" }}>
                        <Typography sx={{ width: 35, flexShrink: 0, fontWeight: 500 }}>
                            Min
                        </Typography>

                        <Slider
                            value={minValue}
                            onChange={handleChangeMin}
                            step={15}
                            min={MIN_S}
                            max={MAX_S}
                            sx={{ flexGrow: 1, minWidth: 260 }}
                            disabled={disabled}
                        />

                        <EditableTimeLabel
                            value={secondsToDayjs(minValue)}
                            onChange={handleMinTimeChange}
                            disabled={disabled}
                            sx={{ width: 80, flexShrink: 0 }} // Larghezza leggermente ridotta dato che non ci sono i bordi laterali
                        />
                    </Box>

                    {/* MAX */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 3, width: "100%" }}>
                        <Typography sx={{ width: 35, flexShrink: 0, fontWeight: 500 }}>
                            Max
                        </Typography>

                        <Slider
                            value={maxValue}
                            onChange={handleChangeMax}
                            step={15}
                            min={MIN_S}
                            max={MAX_S}
                            sx={{ flexGrow: 1, minWidth: 260 }}
                            disabled={disabled}
                        />

                        <EditableTimeLabel
                            value={secondsToDayjs(maxValue)}
                            onChange={handleMaxTimeChange}
                            disabled={disabled}
                            sx={{ width: 80, flexShrink: 0 }}
                        />
                    </Box>
                </Box>
            </Box>
        </LocalizationProvider>
    );
};

export default MinMaxSliders;